import { SelectedDamagedNamePoints } from '@/common/components/adjusterReport/damagedPoints/DamagedPartModal.type';
import Button from '@/common/components/base/button/Button';
import MainModal from '@/common/components/base/modal/Modal';
import { MainModalProps } from '@/common/components/base/modal/Modal.type';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography';
import { damagedPartsSelectedQueryOptions } from '@/common/components/camera/queries/damagedPartsSelected/damagedPartsSelectedQueryOptions';
import { ImageType } from '@/common/components/imageGallery/components/Extensions/moreItemsExtension/moreItemsExtension.type';
import { ImageDataType } from '@/common/components/uploadImage/UploadImage.type';
import UploadImageCard from '@/common/components/uploadImage/UploadImageCard';
import { FileRoleType } from '@/common/types/ApiResponseTypes.type';
import { useAddDamagedPointQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/addDamagedPoint/addDamagedPointQueryOptions';
import { getDamagedAdjusterImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getDamagedAdjusterImages/getDamagedAdjusterImagesQueryOptions';
import useDamagedPoints from '@/modules/adjusterReportModules/IncidentParticipants/store/damagePoints/useDamagedPoints';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';

export default ({
  open,
  closeModal,
  userId,
  roleType,
}: MainModalProps & { userId?: number; roleType: FileRoleType }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const handleCloseModal = () => {
    queryClient.invalidateQueries({
      queryKey: damagedPartsSelectedQueryOptions({
        params: {
          id: +id!,
          damaged_id: +userId!,
          participantRoleType: roleType,
        },
      }).queryKey,
    });
    closeModal();
  };
  return (
    <MainModal closeModal={handleCloseModal} open={open}>
      <PreviewImageAddDamagedPointModal roleType={roleType} userId={userId} />
    </MainModal>
  );
};

const PreviewImageAddDamagedPointModal = ({
  roleType,
  userId,
}: {
  userId?: number;
  roleType: FileRoleType;
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { damagedPoints, resetDamagedPoints } = useDamagedPoints();
  const {
    mutate: addDamagedPoint,
    isPending: loading,
    isSuccess,
  } = useAddDamagedPointQueryOptions();
  const activeDamagedPoints = damagedPoints.filter((item) => !item.disabled);
  const [imageList, setImageList] = useState<ImageDataType[]>(
    activeDamagedPoints.map((item) => ({
      image: '',
      id: +item.id,
      title: { const: '', fa_const: '' },
    })),
  );

  const handleDamagedPointUpload = (
    image: ImageType,
    point: SelectedDamagedNamePoints,
  ) => {
    addDamagedPoint({
      part: +point?.id,
      image: image.blob,
      id: +id!,
      damaged_id: +userId!,
      participantRoleType: roleType,
    });

    setImageList((prevImages) => {
      const filteredImages = prevImages.filter(
        (img) => img.id !== Number(point.id),
      );

      return [
        ...filteredImages,
        {
          image: image.url,
          id: Number(point.id),
          title: {
            const: point.name,
            fa_const: point.name,
          },
        },
      ];
    });
  };

  const handleSubmit = () => {
    if (isSuccess) {
      resetDamagedPoints?.();
      navigate(-1);
      queryClient.invalidateQueries({
        queryKey: getDamagedAdjusterImagesQueryOptions({
          id: +id!,
          enable: true,
          damaged_id: +userId!,
          participantRoleType: roleType,
        }).queryKey,
      });
    }
  };

  return (
    <>
      <TitleDescription>نقاط آسیب‌دیده</TitleDescription>
      <Typography textStyle="b2" mt={24} color="black-1">
        کارشناس محترم، لازم است تصاویر مربوط به هر نقطه آسیب‌دیده را ثبت نمایید.
      </Typography>
      <Flex mt={40} gap={16} flexWrap="wrap" width="100%" alignItems="center">
        {activeDamagedPoints.map((item) => {
          if (item.disabled) return;
          return (
            <Box key={item.id} width="calc(50% - 8px)">
              <UploadImageCard
                photoRotate={false}
                imagesIsShowOnly
                selectFromGallery
                title={item.name}
                imageData={
                  imageList.find((image) => image?.id === Number(item?.id)) || {
                    image: '',
                    id: Number(item.id),
                    title: {
                      const: item.name,
                      fa_const: item.name,
                    },
                  }
                }
                onUploadImage={({ imageData, closeModal }) => {
                  handleDamagedPointUpload(imageData, item);
                  closeModal();
                }}
                uploadImageLoading={loading}
              />
            </Box>
          );
        })}
        <Button
          fullWidth
          onClick={handleSubmit}
          mt={50}
          disabled={!!imageList.find((item) => !item.image)}
        >
          ثبت
        </Button>
      </Flex>
    </>
  );
};
